/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import { DS, DSErrors, DSUtils } from 'js-data';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';

import analyticsCounterResource from '../analytics/analytics-counter-resource';
import analyticsGroupResource from '../analytics/analytics-group-resource';
import assetResource from '../asset/asset-resource';
import contentTaskResource from '../content/task/content-task-resource';
import exceptionResource from '../exception/exception-resource';
import layerResource from '../layer/layer-resource';
import layoutResource from '../layout/layout-resource';
import mediumResource from '../medium/medium-resource';
import playlistResource from '../playlist/playlist-resource';
import powerTaskResource from '../power/task/power-task-resource';
import sessionResource from '../session/session-resource';
import taskResource from '../task/task-resource';
import timerResource from '../timer/timer-resource';
import trackResource from '../track/track-resource';

// TODO #471 Doc: Aktualizovat store diagram (Asset#checksum)
/**
 * Datový sklad postavený na knihovně JSData.
 *
 * ![Diagram tříd](./core/store/store.doc.png)
 * @extends DS
 */
class Store extends DS {
  /**
   * Vytvoří datový sklad.
   * @param {Object.<string, DsAdapter>|null} dsAdapterMap Mapa adaptérů pro JSData.
   * @returns {Store} Nový datový sklad.
   */
  constructor(dsAdapterMap) {
    super({
      // Simple where může způsobit nechtěné operace např. {limit: 1} má filtrovat, nebo stránkovat?
      allowSimpleWhere: false,
      // Načítání z cache pouze na vyžádání. Může způsobit nepříliš očekávané chování.
      bypassCache: true,
      defaultAdapter: 'storage',
      // Optimalizace výkonu. Naslouchat na změny u datových zdrojů.
      instanceEvents: false,
      // Optimalizace výkonu. Deprecated computed properties. Používat raději getter / setter styl.
      watchChanges: false
    });
    // Registrace adaptérů
    if (isObject(dsAdapterMap)) {
      Object.entries(dsAdapterMap).forEach(([key, dsAdapter]) => {
        this.registerAdapter(key, dsAdapter);
      });
    }
    // Registrace datových zdrojů
    this.defineResource(analyticsCounterResource);
    this.defineResource(analyticsGroupResource);
    this.defineResource(assetResource);
    this.defineResource(contentTaskResource);
    this.defineResource(exceptionResource);
    this.defineResource(layerResource);
    this.defineResource(layoutResource);
    this.defineResource(mediumResource);
    this.defineResource(playlistResource);
    this.defineResource(powerTaskResource);
    this.defineResource(sessionResource);
    this.defineResource(taskResource);
    this.defineResource(timerResource);
    this.defineResource(trackResource);
  }

  // TODO #479 Chybí některá funkcionality: notifications, upsert, ...
  // - Viz rozdíly v js-data create funkci.
  // - Asi i zkontrolovat konfiguraci označenou pro #create:
  //   https://www.js-data.io/v2.9/docs/dsdefaults
  /**
   * Vytvoření více objektů persistentně zároveň.
   * Slouží především k optimalizaci výkonu: omezení emitovaných událostí a operací s adaptérem.
   * @param {string} resourceName Název datového zdroje.
   * @param {Array.<DsAttrs>} attrsArray Objekty ke vložení v poli.
   * @param {DsOptions} options Nastavení JSData.
   * @returns {Promise.<Array.<DsModel>>} Odebrané modely.
   */
  createMany(resourceName, attrsArray, options) {
    // Validace vstupů, obdobně jak to dělá DS#create.
    const definition = this.definitions[resourceName];
    if (!definition) {
      throw new DSErrors.NER(resourceName);
    }
    if (!isArray(attrsArray)) {
      throw new DSErrors.IA(
        `${resourceName}.replaceAsync: "attrs" must be an array!`
      );
    }
    // Vyhledání adaptéru, se kterým se bude pracovat.
    const mergedOptions = DSUtils._(definition, options);
    const adapterName = definition.getAdapterName(mergedOptions);
    const adapter = this.adapters[adapterName];
    // Odebrání vybraných hodnot z attrs, obdobně jak to dělá DS#create.
    const omittedAttrsArray = attrsArray.map((attrs) =>
      DSUtils.omit(attrs, mergedOptions.omit)
    );
    // Předpokládá se, že adaptér povinně implementuje createMany funkci.
    // Použije se optimalizovaná createMany funkce adaptéru.
    return (
      adapter
        .createMany(definition, omittedAttrsArray, mergedOptions)
        // Vytvořené JSON attrs se převedou na modely a vloží se do store,
        // pokud mají být cachované, obdobně jak to dělá DS#create.
        .then((createdAttrsArray) => {
          if (!mergedOptions.cacheResponse) {
            // just return an un-injected instances
            return createdAttrsArray.map((attrs) =>
              this.createInstance(resourceName, attrs, mergedOptions)
            );
          }
          // injected created intem into the store
          const createdArray = this.inject(
            definition.name,
            createdAttrsArray,
            mergedOptions.orig()
          );
          // mark item's `find` query as completed, so a subsequent `find` call for this item will resolve immediately
          const resource = this.store[resourceName];
          const { idAttribute } = definition;
          const nowTime = new Date().getTime();
          createdArray.forEach((created) => {
            const id = created[idAttribute];
            resource.completedQueries[id] = nowTime;
            resource.saved[id] = DSUtils.updateTimestamp(resource.saved[id]);
          });
          return createdArray;
        })
    );
  }

  /**
   * Odebrání více objektů persistentně zároveň.
   * Slouží především k optimalizaci výkonu: omezení emitovaných událostí a operací s adaptérem.
   * @param {string} resourceName Název datového zdroje.
   * @param {Array.<DsId>} idArray Identifikátory k odebrání v poli.
   * @param {DsOptions} options Nastavení JSData.
   * @returns {Promise.<Array.<DsModel>>} Odebrané modely.
   */
  destroyMany(resourceName, idArray, options) {
    // TODO #479 Promise.all by taky mohlo dělat problémy se svým paralelním zpracováním a zahazováním chyb.
    // TODO Hotfix implementace. Využívat adapter.destroyMany a ejectMany(idArray).
    // - https://github.com/js-data/js-data/blob/2.10.0/src/datastore/async_methods/destroy.js
    // - https://github.com/js-data/js-data/blob/2.10.0/src/datastore/async_methods/destroyAll.js
    return Promise.all(
      idArray.map((id) => this.destroy(resourceName, id, options))
    );
  }

  /**
   * Odebrání více objektů v paměti zároveň.
   * Slouží především k optimalizaci výkonu: omezení emitovaných událostí a operací s adaptérem.
   * Do budoucna by nemělo využívat eject, ale maximálně optimalizovat výkon hromadným odebráním.
   * Podporuje pouze notify a ne už přímé volání definition.beforeEject + definition.afterEject.
   * Ideálně by asi mělo být přímo součástí eject, stejně jako inject, který podporuje vkládání více objektů.
   * @param {string} resourceName Název datového zdroje.
   * @param {Array.<DsId>} idArray Identifikátory k odebrání v poli.
   * @param {DsOptions} options Nastavení JSData.
   * @returns {Array.<DsModel>} Odebrané modely.
   */
  ejectMany(resourceName, idArray, options) {
    const definition = this.definitions[resourceName];
    if (!definition) {
      throw new DSErrors.NER(resourceName);
    }
    if (!isArray(idArray)) {
      throw new DSErrors.IA(
        `${resourceName}.ejectMany: "idArray" must be an array!`
      );
    }
    // Nalezení příslušných modelů, aby šlo předem určit, jestli emitovat beforeEject.
    const modelArray = idArray
      .map((id) => this.get(resourceName, id))
      .filter((model) => model);
    // Bez nalezených modelů se žádné operace neprovádějí.
    if (!modelArray.length) {
      return [];
    }
    const { idAttribute } = definition;
    // Spojení nastavení ze vstupu a z resource.
    const inheritedOptions = DSUtils._(definition, options);
    // Vyvolání lifecycle události pro všechny modely.
    if (inheritedOptions.notify) {
      definition.emit('DS.beforeEject', definition, modelArray);
    }
    // Odebrání všech modelů pomocí eject bez vyvolání událostí.
    const ejectOptions = { ...options, notify: false };
    const ejectedModels = modelArray.map((model) =>
      this.eject(resourceName, model[idAttribute], ejectOptions)
    );
    // Vyvolání lifecycle události pro všechny modely.
    if (inheritedOptions.notify) {
      definition.emit('DS.afterEject', definition, modelArray);
    }
    return ejectedModels;
  }

  /**
   * Nahradí obsah datového zdroje persistentně za novou sadu objektů.
   * @param {string} resourceName Název datového zdroje.
   * @param {Array.<DsAttrs>} attrsArray Objekty ke vložení v poli.
   * @param {DsParams|null} params Filtrovací kritéria JSData.
   * @param {DsOptions} options Nastavení JSData.
   * @returns {Promise.<Array.<DsModel>>} Nově vložené modely.
   */
  replaceAsync(resourceName, attrsArray, params, options) {
    // Zruší se všechny objekty z perzistentího úložiště.
    return (
      this.destroyAll(resourceName, params, options)
        // Nová sada objektů se vytvoří jednorázově a kompletně.
        .then(() => this.createMany(resourceName, attrsArray, options))
    );
  }
}

export default Store;
